import { useEffect, useState } from 'react';
import { useActualizarTrabajo, useCatalogo, useRegistros, useTemporizador } from '../../api/hooks';
import type { TrabajoDetalle } from '../../api/tipos';
import { mensajeError, useAvisar } from '../../componentes/ui';
import { fechaHora } from '../../lib/fechas';
import { cronometro, duracion, minutos, signoPct } from '../../lib/formato';
import { maquinaDeTrabajo, minutosEstimados, minutosRegistrados } from '../../lib/trabajo';
import { useAhora } from '../../lib/useAhora';

export function Tiempo({ trabajo }: { trabajo: TrabajoDetalle }) {
  const registros = useRegistros(trabajo.id);
  const cat = useCatalogo();
  const { iniciar, detener } = useTemporizador(trabajo.id);
  const actualizar = useActualizarTrabajo(trabajo.id);
  const avisar = useAvisar();
  const lista = registros.data ?? [];
  const abierto = lista.find((r) => !r.fin);
  const ahora = useAhora(1000, Boolean(abierto));
  const [maquinaId, setMaquinaId] = useState(maquinaDeTrabajo(trabajo));
  const [nota, setNota] = useState('');
  const [pasarACortando, setPasarACortando] = useState(true);
  useEffect(() => { setMaquinaId(maquinaDeTrabajo(trabajo)); }, [trabajo.maquinaAsignadaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const real = minutosRegistrados(lista, ahora);
  const est = minutosEstimados(trabajo);
  const max = Math.max(est, real, 1);
  const ofreceCortando = trabajo.estado === 'pagado' || trabajo.estado === 'en_cola';
  const ocupado = iniciar.isPending || detener.isPending;

  const empezar = () => iniciar.mutate({ maquinaId, nota: nota.trim() || undefined }, {
    onSuccess: () => {
      setNota('');
      if (ofreceCortando && pasarACortando) actualizar.mutate({ estado: 'cortando', ...(trabajo.maquinaAsignadaId ? {} : { maquinaAsignadaId: maquinaId }) });
    },
    onError: (e) => avisar(mensajeError(e), 'bad'),
  });
  const parar = () => abierto && detener.mutate({ maquinaId: abierto.maquinaId, nota: nota.trim() || undefined }, {
    onSuccess: (r) => { setNota(''); avisar(`Registrado: ${duracion((Date.parse(r.fin ?? new Date().toISOString()) - Date.parse(r.inicio)) / 1000)}`, 'ok'); },
    onError: (e) => avisar(mensajeError(e), 'bad'),
  });

  return (
    <section className="tarjeta" aria-labelledby="tiempo-t">
      <div className="fila fila--entre">
        <h2 id="tiempo-t">Tiempo en máquina</h2>
        {abierto && <span className="meta">en {cat.maquina.get(abierto.maquinaId)?.nombre}</span>}
      </div>
      <div className="fila fila--entre" style={{ alignItems: 'center' }}>
        <span className={`cronometro${abierto ? ' cronometro--vivo' : ''}`} aria-live="off" role="timer">
          {abierto ? cronometro((ahora - Date.parse(abierto.inicio)) / 1000) : '00:00'}
        </span>
        {abierto
          ? <button type="button" className="btn btn--primario btn--grande" onClick={parar} disabled={ocupado}>■ Detener</button>
          : <button type="button" className="btn btn--naranja btn--grande" onClick={empezar} disabled={ocupado || !maquinaId}>▶ Iniciar</button>}
      </div>
      <div className="campos" style={{ marginTop: 10 }}>
        {!abierto && (
          <div className="campo">
            <label htmlFor="t-maquina">Máquina</label>
            <select id="t-maquina" value={maquinaId} onChange={(e) => setMaquinaId(e.target.value)}>
              {cat.maquinas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
          </div>
        )}
        <div className="campo">
          <label htmlFor="t-nota">Nota (opcional)</label>
          <input id="t-nota" value={nota} onChange={(e) => setNota(e.target.value)} placeholder={abierto ? 'Ajuste de enfoque, 2.ª hoja…' : 'Primera hoja…'} maxLength={200} />
        </div>
      </div>
      {!abierto && ofreceCortando && (
        <label className="check" style={{ marginTop: 8 }}>
          <input type="checkbox" checked={pasarACortando} onChange={(e) => setPasarACortando(e.target.checked)} /> Pasar el trabajo a “Cortando” al iniciar
        </label>
      )}

      <div className="comparar-barras" aria-label="Minutos estimados contra reales">
        <div><span className="muted">Estimado</span><span className="barra" style={{ width: `${(est / max) * 100}%`, background: 'var(--serie-1)' }} /><span className="num">{minutos(est)}</span></div>
        <div><span className="muted">Real</span><span className="barra" style={{ width: `${(real / max) * 100}%`, background: 'var(--serie-2)' }} /><span className="num">{real ? minutos(real) : '—'}</span></div>
      </div>
      {real > 0 && <p className="meta">Real vs estimado: {signoPct((real - est) / (est || 1))}</p>}

      {lista.length > 0 && (
        <table className="comparar" style={{ marginTop: 10 }}>
          <thead><tr><th>Inicio</th><th className="oc2">Máquina</th><th className="num">Duración</th></tr></thead>
          <tbody>
            {[...lista].reverse().map((r) => (
              <tr key={r.id}>
                <td className="celda-2">{fechaHora(r.inicio)}{r.nota && <small>{r.nota}</small>}</td>
                <td className="oc2">{cat.maquina.get(r.maquinaId)?.nombre ?? '—'}</td>
                <td className="num">{r.fin ? duracion((Date.parse(r.fin) - Date.parse(r.inicio)) / 1000) : <b style={{ color: 'var(--accent)' }}>{cronometro((ahora - Date.parse(r.inicio)) / 1000)}</b>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
